import Component from '@glimmer/component';
import { service } from '@ember/service';

import { modifier } from 'ember-modifier';
import { colorScheme } from 'ember-primitives/color-scheme';

import { createChart } from './chart';
import { format } from './util';

import type { FormattedData } from './util';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import type { DownloadsResponse } from 'package-majors/types';

const renderChart = modifier((element: HTMLCanvasElement, [data]: [FormattedData[]]) => {
  let chart = createChart(element, data);
  let update = () => chart.update();

  colorScheme.on.update(update);

  return () => {
    colorScheme.off.update(update);
    chart.destroy();
  };
});

const DataChart: TOC<{
  Args: {
    data: FormattedData[];
  };
}> = <template>
  <div
    style="
      display: grid;
      min-width: 100%;
      justify-content: center;
    "
  ><canvas {{renderChart @data}}></canvas></div>
</template>;

export class Data extends Component<{
  Args: {
    data: {
      packages: string[];
      stats: DownloadsResponse[];
    };
  };
}> {
  <template>
    {{#if @data.stats}}
      <DataChart @data={{this.formattedData}} />
    {{/if}}
  </template>

  @service declare router: RouterService;

  get groupBy(): 'minors' | 'majors' {
    let qps = this.router.currentRoute?.queryParams;

    if (qps?.['minors']) {
      return 'minors';
    }

    return 'majors';
  }

  get showOld(): boolean {
    let qps = this.router.currentRoute?.queryParams;

    return qps?.['old'] === 'on';
  }

  get granularity(): 'monthly' | 'daily' | 'total' {
    let qps = this.router.currentRoute?.queryParams;

    const granularity = qps?.['granularity'] ?? 'monthly';

    if (granularity === 'monthly' || granularity === 'daily' || granularity === 'total') {
      return granularity;
    }

    return 'monthly';
  }

  get formattedData() {
    return format(this.args.data.stats, this.groupBy, this.showOld, this.granularity);
  }
}
